import { differenceInCalendarDays } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import getLocale from '../i18n/available-locales';

export const USER_TIMEZONE_KEY = 'USER_TIMEZONE';

// Supported timezones (IANA format)
export const SUPPORTED_TIMEZONES = [
  'UTC',
  'America/Sao_Paulo',
  'America/New_York',
  'America/Los_Angeles',
  'America/Chicago',
  'America/Denver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Dubai',
  'Asia/Bangkok',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

/**
 * Get timezone stored in localStorage, or null if not set
 */
export function getStoredTimezone(): string | null {
  try {
    return localStorage.getItem(USER_TIMEZONE_KEY);
  } catch {
    return null;
  }
}

/**
 * Detect browser timezone using Intl API
 */
export function detectBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get the active timezone following priority:
 * 1. localStorage.USER_TIMEZONE
 * 2. Browser detected timezone
 * 3. UTC fallback
 */
export function getUserTimezone(): string {
  const stored = getStoredTimezone();
  if (stored) return stored;

  const detected = detectBrowserTimezone();
  if (detected && SUPPORTED_TIMEZONES.includes(detected)) {
    return detected;
  }

  return 'UTC';
}

/**
 * Save timezone to localStorage
 */
export function setUserTimezone(timezone: string): void {
  if (!SUPPORTED_TIMEZONES.includes(timezone)) {
    console.warn(`Timezone ${timezone} not in supported list`);
    return;
  }
  try {
    localStorage.setItem(USER_TIMEZONE_KEY, timezone);
  } catch (err) {
    console.error('Failed to save timezone:', err);
  }
}

/**
 * Get list of supported timezones
 */
export function getSupportedTimezones(): string[] {
  return [...SUPPORTED_TIMEZONES];
}

/**
 * Format a UTC datetime for display in user's timezone
 * @param value - UTC date/string/timestamp (or null/undefined)
 * @param formatPattern - date-fns format pattern (from i18n)
 * @param options - optional timezone or locale override
 * @returns Formatted string in user's timezone
 */
export function formatDateTime(
  value: string | Date | number | null | undefined,
  formatPattern: string,
  options?: { timezone?: string; locale?: any }
): string {
  if (!value) {
    return '';
  }

  try {
    const timezone = options?.timezone || getUserTimezone();
    const locale = getLocale(options?.locale || undefined);

    // Format using formatInTimeZone which handles UTC to timezone conversion
    return formatInTimeZone(value, timezone, formatPattern, {
      locale,
    });
  } catch (err) {
    console.error('Error formatting date:', err);
    return '';
  }
}

/**
 * Convert a UTC date to Date object in user's timezone (for internal use)
 */
export function utcToUserTz(value: string | Date | number): Date {
  try {
    const timezone = getUserTimezone();
    return toZonedTime(value, timezone);
  } catch (err) {
    console.error('Error converting UTC to timezone:', err);
    return new Date(value);
  }
}

/**
 * Helper: difference in days between two dates
 */
export function differenceInDays(dateLeft: Date, dateRight: Date): number {
  return differenceInCalendarDays(dateLeft, dateRight);
}
